import React, {useState} from 'react';
import {Modal, Button, Form, Spinner, InputGroup} from 'react-bootstrap';

interface AddAliasModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (alias: string) => Promise<void>;
  accountEmail: string;
  domainName: string;
}

const AddAliasModal: React.FC<AddAliasModalProps> = ({
  show,
  onHide,
  onAdd,
  accountEmail,
  domainName,
}) => {
  const [aliasLocal, setAliasLocal] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateLocalPart = (local: string): boolean => {
    // Validation de la partie locale uniquement (avant le @)
    const localPartRegex = /^[a-zA-Z0-9._-]+$/;
    return localPartRegex.test(local);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!aliasLocal.trim()) {
      setError('La partie locale de l\'alias est obligatoire');
      return;
    }

    if (!validateLocalPart(aliasLocal)) {
      setError('Format invalide. Utilisez uniquement des lettres, chiffres, points, tirets et underscores');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onAdd(aliasLocal);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'ajout de l\'alias');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setAliasLocal('');
      setError('');
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Ajouter un alias</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="alert alert-info mb-3">
            <i className="fa-solid fa-info-circle me-2"></i>
            Ajout d'un alias pour : <strong>{accountEmail}</strong>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>
              Adresse email de l'alias <span className="text-danger">*</span>
            </Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type="text"
                placeholder="alias"
                value={aliasLocal}
                onChange={(e) => {
                  setAliasLocal(e.target.value);
                  setError('');
                }}
                isInvalid={!!error}
                autoFocus
                disabled={isLoading}
                required
                pattern="[a-zA-Z0-9._-]+"
              />
              <InputGroup.Text className="bg-light">
                <strong>@{domainName.toLowerCase()}</strong>
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {error || 'Veuillez saisir la partie locale de l\'adresse email.'}
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              L'adresse complète sera : <strong>{aliasLocal || 'alias'}@{domainName.toLowerCase()}</strong>
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Ajout en cours...
              </>
            ) : (
              <>
                <i className="fa-solid fa-plus me-2"></i>
                Ajouter
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddAliasModal;
